import React, { useCallback, useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TextField,
  Tooltip,
  Typography,
  InputAdornment,
} from '@mui/material';
import {
  Add as AddIcon,
  Delete as DeleteIcon,
  Edit as EditIcon,
  Search as SearchIcon,
  Category as CategoryIcon,
} from '@mui/icons-material';
import { financeService } from '../services/financeService';
import { useSubmitLock } from '../hooks/useSubmitLock';

const classificationLabels = {
  custo_fixo: 'Custo Fixo',
  custo_variavel: 'Custo Variável',
  despesa_fixa: 'Despesa Fixa',
  despesa_variavel: 'Despesa Variável',
  receita: 'Receita',
  imposto: 'Imposto',
  retirada_lucros: 'Retirada lucros',
  ativo_imobilizado: 'Ativo imobilizado',
};

const dreCategoryLabels = {
  receita_bruta: 'Receita Bruta',
  deducoes_abatimentos: 'Deduções e Abatimentos (impostos sobre vendas)',
  csp: 'Custos dos Serviços Prestados (CSP)',
  despesas_vendas: 'Despesas com Vendas',
  despesas_administrativas: 'Despesas Administrativas',
  despesas_financeiras: 'Despesas Financeiras',
  provisoes_irpj_csll: 'Provisões para IRPJ e CSLL (impostos sobre o lucro)',
};

function SubclassificationsTab() {
  const [submitting, runSubmit] = useSubmitLock();
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);
  const [loading, setLoading] = useState(false);

  const [searchQuery, setSearchQuery] = useState('');
  const [activeFilter, setActiveFilter] = useState(undefined);
  const [classificationFilter, setClassificationFilter] = useState('');

  const [openDialog, setOpenDialog] = useState(false);
  const [dialogMode, setDialogMode] = useState('create');
  const [currentItem, setCurrentItem] = useState(null);

  const [formData, setFormData] = useState({
    classification: '',
    name: '',
    is_active: true,
    notes: '',
    dre_category: ''
  });

  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  const loadItems = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const response = await financeService.getSubclassificationsPage({
        q: searchQuery,
        classification: classificationFilter || undefined,
        is_active: activeFilter,
        offset: page * rowsPerPage,
        limit: rowsPerPage,
      });
      setItems(response.data || []);
      setTotal(response.total || 0);
    } catch (err) {
      console.error('Erro ao carregar subclassificações:', err);
      setError('Erro ao carregar subclassificações. Tente novamente.');
    } finally {
      setLoading(false);
    }
  }, [page, rowsPerPage, searchQuery, classificationFilter, activeFilter]);

  useEffect(() => {
    loadItems();
  }, [loadItems]);

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleSearch = (event) => {
    setSearchQuery(event.target.value);
    setPage(0);
  };

  const handleActiveFilter = (event) => {
    const value = event.target.value;
    setActiveFilter(value === '' ? undefined : value === 'true');
    setPage(0);
  };

  const handleClassificationFilter = (event) => {
    setClassificationFilter(event.target.value);
    setPage(0);
  };

  const handleOpenDialog = (mode, item = null) => {
    setDialogMode(mode);
    setCurrentItem(item);

    if (mode === 'edit' && item) {
      setFormData({
        classification: item.classification || '',
        name: item.name || '',
        is_active: item.is_active !== undefined ? item.is_active : true,
        notes: item.notes || '',
        dre_category: item.dre_category || ''
      });
    } else {
      setFormData({
        classification: classificationFilter || '',
        name: '',
        is_active: true,
        notes: '',
        dre_category: ''
      });
    }

    setOpenDialog(true);
    setError('');
    setSuccess('');
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setCurrentItem(null);
    setError('');
    setSuccess('');
  };

  const handleFormChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const cleanFormData = (data) => {
    const cleaned = {};
    Object.keys(data).forEach(key => {
      const value = data[key];
      if (typeof value === 'boolean') {
        cleaned[key] = value;
      } else if (value !== '' && value !== null && value !== undefined) {
        cleaned[key] = value;
      }
    });
    return cleaned;
  };

  const handleSubmit = async () => {
    setError('');
    setSuccess('');

    if (!formData.classification) {
      setError('Classificação é obrigatória');
      return;
    }
    if (!formData.name.trim()) {
      setError('Nome é obrigatório');
      return;
    }

    try {
      const payload = cleanFormData({
        ...formData,
        name: formData.name.trim(),
      });

      if (dialogMode === 'create') {
        await financeService.createSubclassification(payload);
        setSuccess('Subclassificação criada com sucesso!');
      } else {
        await financeService.updateSubclassification(currentItem.id, payload);
        setSuccess('Subclassificação atualizada com sucesso!');
      }

      handleCloseDialog();
      loadItems();
    } catch (err) {
      console.error('Erro ao salvar subclassificação:', err);
      const errorMessage = err.response?.data?.detail
        || (Array.isArray(err.response?.data) ? err.response.data.map(e => e.msg).join(', ') : null)
        || 'Erro ao salvar subclassificação. Tente novamente.';
      setError(errorMessage);
    }
  };

  const handleDelete = async (item) => {
    if (!window.confirm(`Tem certeza que deseja excluir a subclassificação "${item.name}"?`)) {
      return;
    }

    try {
      await financeService.deleteSubclassification(item.id);
      setSuccess('Subclassificação excluída com sucesso!');
      loadItems();
    } catch (err) {
      console.error('Erro ao excluir subclassificação:', err);
      setError(err.response?.data?.detail || 'Erro ao excluir subclassificação. Tente novamente.');
    }
  };

  return (
    <Box>
      {error && (
        <Alert severity="error" onClose={() => setError('')} sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}
      {success && (
        <Alert severity="success" onClose={() => setSuccess('')} sx={{ mb: 2 }}>
          {success}
        </Alert>
      )}

      <Card
        elevation={0}
        sx={{
          mb: 3,
          background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
          color: 'white',
          borderRadius: 3
        }}
      >
        <CardContent sx={{ p: 3 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 2 }}>
            <Box>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                <CategoryIcon sx={{ fontSize: 32 }} />
                <Typography variant="h5" fontWeight="bold">
                  Subclassificações
                </Typography>
              </Box>
              <Typography variant="body2" sx={{ opacity: 0.9 }}>
                Subcategorias por classificação (ex.: Custo Fixo → Salário)
              </Typography>
            </Box>

            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={() => handleOpenDialog('create')}
              sx={{
                bgcolor: 'rgba(255, 255, 255, 0.2)',
                backdropFilter: 'blur(10px)',
                border: '1px solid rgba(255, 255, 255, 0.3)',
                '&:hover': { bgcolor: 'rgba(255, 255, 255, 0.3)' },
              }}
            >
              Nova Subclassificação
            </Button>
          </Box>
        </CardContent>
      </Card>

      <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', mb: 2 }}>
        <TextField
          placeholder="Buscar por nome..."
          value={searchQuery}
          onChange={handleSearch}
          size="small"
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon fontSize="small" />
              </InputAdornment>
            )
          }}
          sx={{ minWidth: 280 }}
        />

        <FormControl size="small" sx={{ minWidth: 220 }}>
          <InputLabel>Classificação</InputLabel>
          <Select value={classificationFilter} label="Classificação" onChange={handleClassificationFilter}>
            <MenuItem value="">Todas</MenuItem>
            {Object.entries(classificationLabels).map(([value, label]) => (
              <MenuItem key={value} value={value}>{label}</MenuItem>
            ))}
          </Select>
        </FormControl>

        <FormControl size="small" sx={{ minWidth: 180 }}>
          <InputLabel>Status</InputLabel>
          <Select value={activeFilter === undefined ? '' : String(activeFilter)} label="Status" onChange={handleActiveFilter}>
            <MenuItem value="">Todos</MenuItem>
            <MenuItem value="true">Ativos</MenuItem>
            <MenuItem value="false">Inativos</MenuItem>
          </Select>
        </FormControl>

        {(searchQuery || classificationFilter || activeFilter !== undefined) && (
          <Chip
            label="Limpar"
            onClick={() => {
              setSearchQuery('');
              setClassificationFilter('');
              setActiveFilter(undefined);
              setPage(0);
            }}
            variant="outlined"
          />
        )}
      </Box>

      <TableContainer component={Box} sx={{ border: '1px solid rgba(0,0,0,0.12)', borderRadius: 2 }}>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell><strong>Classificação</strong></TableCell>
              <TableCell><strong>Nome</strong></TableCell>
              <TableCell><strong>Linha DRE</strong></TableCell>
              <TableCell><strong>Status</strong></TableCell>
              <TableCell><strong>Observações</strong></TableCell>
              <TableCell align="right"><strong>Ações</strong></TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {loading ? (
              <TableRow>
                <TableCell colSpan={6} align="center" sx={{ py: 4 }}>
                  <CircularProgress size={24} />
                </TableCell>
              </TableRow>
            ) : items.length === 0 ? (
              <TableRow>
                <TableCell colSpan={6} align="center" sx={{ py: 4 }}>
                  <Typography variant="body2" color="text.secondary">Nenhuma subclassificação encontrada</Typography>
                </TableCell>
              </TableRow>
            ) : (
              items.map((item) => (
                <TableRow key={item.id} hover>
                  <TableCell>
                    <Chip
                      size="small"
                      label={classificationLabels[item.classification] || item.classification}
                      variant="outlined"
                    />
                  </TableCell>
                  <TableCell>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <CategoryIcon fontSize="small" color="action" />
                      <Typography variant="body2" fontWeight={600}>{item.name}</Typography>
                    </Box>
                  </TableCell>
                  <TableCell>
                    {item.dre_category ? (
                      <Chip
                        size="small"
                        label={dreCategoryLabels[item.dre_category] || item.dre_category}
                        color="primary"
                        variant="outlined"
                        sx={{ maxWidth: 260, '& .MuiChip-label': { whiteSpace: 'normal' } }}
                      />
                    ) : (
                      <Typography variant="body2" color="text.disabled">Padrão</Typography>
                    )}
                  </TableCell>
                  <TableCell>
                    <Chip
                      size="small"
                      label={item.is_active ? 'Ativo' : 'Inativo'}
                      color={item.is_active ? 'success' : 'default'}
                      variant={item.is_active ? 'filled' : 'outlined'}
                    />
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2" color="text.secondary" sx={{ maxWidth: 280 }} noWrap>
                      {item.notes || '-'}
                    </Typography>
                  </TableCell>
                  <TableCell align="right">
                    <Tooltip title="Editar">
                      <Button
                        size="small"
                        startIcon={<EditIcon fontSize="small" />}
                        onClick={() => handleOpenDialog('edit', item)}
                      >
                        Editar
                      </Button>
                    </Tooltip>
                    <Tooltip title="Excluir">
                      <Button
                        size="small"
                        color="error"
                        startIcon={<DeleteIcon fontSize="small" />}
                        onClick={() => handleDelete(item)}
                      >
                        Excluir
                      </Button>
                    </Tooltip>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>

      <TablePagination
        component="div"
        count={total}
        page={page}
        onPageChange={handleChangePage}
        rowsPerPage={rowsPerPage}
        onRowsPerPageChange={handleChangeRowsPerPage}
        rowsPerPageOptions={[10, 25, 50, 100]}
        labelRowsPerPage="Linhas"
      />

      <Dialog open={openDialog} onClose={handleCloseDialog} fullWidth maxWidth="sm">
        <DialogTitle>
          {dialogMode === 'create' ? 'Nova Subclassificação' : 'Editar Subclassificação'}
        </DialogTitle>
        <DialogContent dividers>
          <Box sx={{ display: 'grid', gap: 2 }}>
            <FormControl fullWidth>
              <InputLabel>Classificação</InputLabel>
              <Select
                value={formData.classification}
                onChange={(e) => handleFormChange('classification', e.target.value)}
                label="Classificação"
                required
              >
                {Object.entries(classificationLabels).map(([value, label]) => (
                  <MenuItem key={value} value={value}>{label}</MenuItem>
                ))}
              </Select>
            </FormControl>

            <TextField
              fullWidth
              label="Nome"
              value={formData.name}
              onChange={(e) => handleFormChange('name', e.target.value)}
              required
            />

            <FormControl fullWidth>
              <InputLabel>Linha DRE (opcional)</InputLabel>
              <Select
                value={formData.dre_category}
                onChange={(e) => handleFormChange('dre_category', e.target.value)}
                label="Linha DRE (opcional)"
              >
                <MenuItem value="">
                  <em>Padrão (definido pela classificação macro)</em>
                </MenuItem>
                {Object.entries(dreCategoryLabels).map(([value, label]) => (
                  <MenuItem key={value} value={value}>{label}</MenuItem>
                ))}
              </Select>
            </FormControl>

            <TextField
              fullWidth
              label="Observações"
              value={formData.notes}
              onChange={(e) => handleFormChange('notes', e.target.value)}
              multiline
              minRows={3}
            />

            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Switch
                checked={!!formData.is_active}
                onChange={(e) => handleFormChange('is_active', e.target.checked)}
              />
              <Typography variant="body2">Ativo</Typography>
            </Box>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog}>Cancelar</Button>
          <Button
            onClick={() => runSubmit(handleSubmit)}
            variant="contained"
            disabled={submitting || !formData.classification || !formData.name.trim()}
          >
            Salvar
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

export default SubclassificationsTab;
